<template>
    <TooltipProvider :delay-duration="400">
        <div class="relative flex h-dvh w-full flex-col overflow-hidden">
            <!-- Aurora background — speedtest preset with 6-hue palette + 6
             nuclei. The Aurora belongs to *hero* surfaces — immersive,
             single-card compositions. A dense data panel is the opposite
             kind of surface: it must recede so tabular data reads, and the
             dashboard layouts paint their own opaque `bg-background`
             chassis for exactly that. So the Aurora is suppressed on the
             *data-panel* routes (`isDataPanelRoute`) and mounts on every
             other route — including `/admin/login`, which is NOT a data
             panel but the admin auth-entry hero: a single floating glass
             card that needs the moving backdrop to read as glass and to
             carry the brand. The genuine `/admin/*` dashboard children sit
             inside `AdminDashboardLayout`'s opaque chassis and stay
             aurora-free; the login route has no such chassis.

             AV.W16 — REVERSAL of AK-W6-α (A3 §6.R-9). The per-route `0.5`
             opacity-ceiling that muted the *content-over-aurora* flow routes
             (survey, thankyou, admin-login) is RETIRED: the user rejects the
             muted register — the wash read as under-present, receding into
             invisibility behind those forms. The ceiling now reverts to the
             component's `1.0` default uniformly (the `:opacity-ceiling` bind is
             dropped), so the drift regains full PRESENCE on every aurora route.
             This is the presence-CEILING axis only; the contrast FLOOR (forms
             stay legible) is held by the amended `ALPHA_LIGHT` 0.13 / `ALPHA_DARK`
             0.14 wash + the survey/thankyou spine chassis, NOT by this ceiling —
             the two axes are orthogonal (DDR-AV-W16-AURORA-DERIVE §Reversal). The
             now-unused `auroraOpacityCeiling` computed in `useRouteTransition.ts`
             is W19-reserved (it carves that file for the route-VT `transitionKey`);
             its retirement is forwarded to W19 rather than edited through here. -->
            <!-- Aurora — async-component wrap (AH.W3-C). The placeholder
             below holds the same z-stack slot (`fixed inset-0 z-behind`)
             until rIC flips `auroraReady`, at which point the async
             component resolves and the WebGL surface paints over the
             gradient backdrop. Aurora's chunk + its `keyframes` transitive
             dependency leave the entry-chunk's static import graph; the
             user sees the same gradient on the first frame either way
             (glass-ui's `Aurora` paints a CSS-gradient critical-inline
             placeholder at HTML-parse time, and this placeholder mirrors
             that slot). No CLS — `fixed inset-0` is out of flow. -->
            <div
                v-if="!isDataPanelRoute && !auroraReady"
                class="aurora-placeholder z-behind pointer-events-none fixed
                    inset-0 h-full w-full"
                aria-hidden="true"
            />
            <Aurora
                v-if="!isDataPanelRoute && auroraReady"
                ref="auroraRef"
                class="z-behind pointer-events-none fixed inset-0 h-full w-full"
                :config="auroraConfig"
                :render-mode="auroraRenderMode"
                @init-error="onAuroraInitError"
            />

            <!-- Route content area. `<main>` is a flex column that fills the
             viewport height; the `<RouterView>` slot stretches via
             `flex-1` so the route owns the available column space, and
             `<Dock>` renders inline at the bottom of the same column.
             Inline flow (vs `position: fixed`) lets the card naturally
             fill or sit centred without leaving dead vertical space
             between card-bottom and dock-top.

             The fade `<Transition>` consumes glass-ui's canonical
             `fade` (PRM-bracketed upstream). `<main>` is the single
             landmark for axe `landmark-one-main`. -->
            <main class="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
                <!-- AK-W2-β2 — instrument-chassis spine. I hoist
                 `<InstrumentChassis>` above the RouterView so the chassis
                 bezel + `--phase-color` cascade persist across the
                 `speedtest → survey → thankyou` route transitions; each
                 view slots into the chassis `#dial` via the RouterView,
                 and the route-level `pane-slide` <Transition> animates
                 the dial contents inside the persistent chassis frame
                 rather than re-mounting the chassis on every navigation.
                 Per W2-β1 (glass-ui 3822f48) the `<header>` /
                 `<footer>` / dividers are slot-gated on
                 `$slots.strip` / `$slots.control`, so this App-level
                 mount — which binds only `#dial` — emits no empty
                 regions and the consumer-side suppression CSS retires.
                 Per W2-β2 / G-AK-D2 the chassis is suppressed on
                 data-panel routes (mirrors the `isDataPanelRoute`
                 Aurora suppression) — those surfaces paint their own
                 opaque dashboard chassis.

                 AL-W2 W1-β (G-AL-D1 Path A + G-AL-D2 Path A): the chassis
                 also opts OUT on `/admin/login` per the auth-entry
                 narrative-fit (T7) — admin-login is its own register, not
                 a journey phase. Admin-login KEEPS the Aurora (it stays
                 outside `isDataPanelRoute`) so the floating glass card
                 still reads against the moving backdrop. AL-W1 (glass-ui
                 b25fb11) ships an 8th tier `variant="spine"` for the
                 HOUSING register; I bind `chassisVariant` so quiet-content
                 routes (survey / thankyou) get the polished sleeve while
                 the speedtest dial route keeps the default `"glass"`
                 (the Aurora-vivid brand register paints the housing). -->
                <InstrumentChassis
                    v-if="!isChassisSuppressedRoute"
                    :variant="chassisVariant"
                    :phase="chassisPhase"
                    class="app-chassis-spine"
                >
                    <template #dial>
                        <RouterView v-slot="{ Component, route: viewRoute }">
                            <Transition
                                :name="routeTransitionName(viewRoute)"
                                mode="out-in"
                            >
                                <!-- The DIAL route centres its content
                                 cross-axis: the speedtest card carries its
                                 own intrinsic min-height (60dvh-clamped) and
                                 centring distributes residual vertical space
                                 evenly top + bottom rather than leaving dead
                                 space between card and dock. Admin-login (the
                                 only admin route that lands in the chassis
                                 branch — its surface is a hero, not a data
                                 panel) keeps `''` so the admin-login view owns
                                 its own layout.

                                 AS-1 (top-anchor): the survey + thankyou
                                 cockpits are NOT the square dial — they are a
                                 `flex-1` form/result card whose result-pill
                                 cluster should hug the TOP of the column, not
                                 float at vertical centre. So these two routes
                                 top-hug via `items-start` (keeping horizontal
                                 `justify-center`) while the dial route keeps
                                 `items-center`. Extends the admin route-branch
                                 cleanly via route.name. -->
                                <div
                                    :key="transitionKey(viewRoute)"
                                    class="flex min-h-0 w-full flex-1"
                                    :class="
                                        viewRoute.name === 'survey' ||
                                        viewRoute.name === 'thankyou'
                                            ? 'items-start justify-center'
                                            : viewRoute.name &&
                                                typeof viewRoute.name ===
                                                    'string' &&
                                                viewRoute.name.startsWith(
                                                    'admin',
                                                )
                                              ? ''
                                              : 'items-center justify-center'
                                    "
                                >
                                    <component
                                        :is="Component"
                                        :ref="setViewRef"
                                    />
                                </div>
                            </Transition>
                        </RouterView>
                    </template>
                </InstrumentChassis>

                <!-- Data-panel routes (admin + public dashboard children)
                 mount the RouterView WITHOUT the chassis spine: those
                 surfaces own their opaque `bg-background` chassis and
                 the instrument-cluster frame would compete with the
                 dashboard's own layout. Mirrors the Aurora suppression
                 logic above so the two suppressions stay in lockstep. -->
                <RouterView v-else v-slot="{ Component, route: viewRoute }">
                    <Transition
                        :name="routeTransitionName(viewRoute)"
                        mode="out-in"
                    >
                        <div
                            :key="transitionKey(viewRoute)"
                            class="flex min-h-0 w-full flex-1"
                        >
                            <component :is="Component" :ref="setViewRef" />
                        </div>
                    </Transition>
                </RouterView>

                <!-- Bottom dock — hidden on dashboard routes; carries
                 Back · contextual [Start | Stop | Retake + Next] across
                 speedtest/survey/thankyou phases. Rendered inline at
                 the column bottom; `--dock-inset` drives the dock's
                 interior padding (the gap between content edge and the
                 floating pill). The dock floats visually because its
                 inner host carries `pointer-events-none` on the column
                 with the pill itself sitting in `pointer-events-auto`. -->
                <Dock
                    v-if="!isDashboardRoute"
                    :current-view="currentView"
                    :speedtest-status="speedtestStatus"
                    :can-go-back="canGoBack"
                    :survey-state="surveyState"
                    :test-only="isTestOnly"
                    @start="onDockStart"
                    @stop="onDockStop"
                    @next="onDockForward"
                    @retake="onDockRetake"
                    @back="onDockBack"
                    @forward="onDockForward"
                />
            </main>

            <!-- Toaster — inlined here at AI.W5-ζ (drops the zero-behaviour
             ToastProvider SFC wrapper). The upstream `<Toaster>` reads
             theme from the document `.dark` cascade directly; no
             `isDark` prop bridge needed. The `<Teleport to="html">`
             keeps the toast layer outside the app's transform contexts.
             First-toast is post-test so the bundle weight is paid
             post-LCP regardless of eagerness. -->
            <Teleport to="html">
                <Toaster position="top-center" />
            </Teleport>

            <!-- Cellular-warning Dialog lazy-imports through
             `defineAsyncComponent`; the reka-ui dialog primitives carve
             out of the entry chunk and render only on the engine's
             `cellular: true` branch. -->
            <Suspense v-if="showCellularWarning">
                <CellularWarningDialog
                    :open="showCellularWarning"
                    @update:open="showCellularWarning = $event"
                    @cancel="showCellularWarning = false"
                    @proceed="onCellularWarningProceed"
                />
            </Suspense>
        </div>
    </TooltipProvider>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick, defineAsyncComponent } from "vue";
import { useRouter, RouterView } from "vue-router";
import type { AuroraRenderMode } from "@mkbabb/glass-ui/aurora";

// AH.W3-C: `Aurora` is the only entry-graph static edge into the
// `keyframes-*.js` chunk (~102 KB raw / ~35 KB gz) — `Aurora` → glass-ui's
// `useAnimatedNumber` → keyframes engine. Carve via `defineAsyncComponent` so
// Rollup re-emits keyframes as a *dynamic-only* leaf and the entry chunk's
// static import graph no longer reaches it. The `auroraReady` rIC gate then
// holds the chunk fetch off the LCP frame entirely (rIC fires post-first-paint
// on Chromium/Firefox; Safari falls through to `setTimeout(..., 0)` — same
// "after first paint" intent). The same-paint-position placeholder
// (`aurora-placeholder` — `pointer-events-none fixed inset-0 z-behind`) keeps
// the slot occupied with the existing CSS-gradient backdrop so swapping in the
// WebGL surface causes no CLS.
const Aurora = defineAsyncComponent(() =>
    import("@mkbabb/glass-ui/aurora").then((m) => m.Aurora),
);

// `TooltipProvider` + `Toaster` are the only glass-ui primitives App.vue
// mounts eagerly — the Dialog cluster still lazy-loads via
// `defineAsyncComponent` so the reka-ui dialog primitives carve out of
// the entry chunk. `Toaster` is inlined here at AI.W5-ζ (retired the
// zero-behaviour `ToastProvider` SFC wrapper); the upstream
// `<Toaster>` reads the dark cascade from `document.documentElement`
// directly, so the prior `:is-dark` prop bridge was already cosmetic.
import { TooltipProvider } from "@mkbabb/glass-ui/tooltip";
import { Toaster } from "@mkbabb/glass-ui/toast";
import { InstrumentChassis } from "@mkbabb/glass-ui/instrument-chassis";
import { useIdleReady } from "@mkbabb/glass-ui/dom";
import Dock from "@src/components/Dock.vue";

const CellularWarningDialog = defineAsyncComponent(
    () => import("@src/components/CellularWarningDialog.vue"),
);

import { useResumeProtocol } from "@src/components/survey/composables/useResumeProtocol.ts";
import { useFunnelAbandon } from "@src/components/survey/composables/useFunnelAbandon.ts";
import { useJourneyStore } from "@src/stores/useJourneyStore.ts";
import { useSpeedtestStore } from "@features/speedtest";

// ── Decomposed concerns (AM-W3-ζ god-module decomp) ──────────────────
//
// App.vue is the lean orchestrator that composes three cohesive seams:
//   - `useRouteTransition` — route/view derivation + pane-slide Transition
//     vocabulary + chassis register (phase + variant).
//   - `useAppProviders`    — substrate Pinia singletons + dark cascade +
//     the IP/connection/geolocation provider instances and their provides.
//   - `useDockOrchestration` — bottom-dock state/actions, the routed
//     view-ref bridge, the cellular-warning gate, and the DockStart/Forward
//     provides.
import { useRouteTransition } from "@src/composables/useRouteTransition.ts";
import { useAppProviders } from "@src/composables/useAppProviders.ts";
import { useDockOrchestration } from "@src/composables/useDockOrchestration.ts";

import { useSpeedtestAuroraConfig } from "@src/config/auroraConfig.ts";

// Dev-only Playwright fixture handle. `import.meta.env.DEV` gates the
// install call below so production builds tree-shake the entire
// surface.
import { useSpeedtestDevHandle } from "@src/composables/useSpeedtestDevHandle.ts";

import "@design/index.css";
// AJ-W2-δ — pane-slide vocabulary (the brand's "page-turn" gestalt at
// the route-level <Transition> + the SpeedtestResults complete-cluster
// enter + the SurveyWizard step direction watcher). Speedtest-local
// today; promotion to glass-ui's transitions.css queued at W6 per
// RATIFIED.md sequence. See `src/design/animations.css` for the
// 4 directional contracts (`pane-slide`, `pane-slide-back`,
// `pane-slide-left`, `pane-slide-right`) + the PRM bracket.
import "@design/animations.css";

const router = useRouter();

// ── Route/view derivation + Transition + chassis register ────────────
//
// AV.W16 — `auroraOpacityCeiling` is no longer destructured here: the per-route
// `0.5` muted ceiling is reversed (see the `<Aurora>` mount comment), so the
// ceiling reverts to the component's `1.0` default uniformly and there is no
// consumer for the computed. The computed itself still lives in the W19-reserved
// `useRouteTransition.ts`; its retirement is forwarded to W19.
const {
    currentView,
    isDataPanelRoute,
    isChassisSuppressedRoute,
    isDashboardRoute,
    transitionKey,
    routeTransitionName,
    chassisPhase,
    chassisVariant,
} = useRouteTransition();

// ── App-level providers (substrate stores + dark + provider instances) ─
const { connectionType } = useAppProviders();

// ── Bottom-dock orchestration (state, actions, cellular warning) ─────
const {
    setViewRef,
    speedtestStatus,
    isTestOnly,
    canGoBack,
    surveyState,
    onDockStart,
    onDockForward,
    onDockBack,
    onDockStop,
    onDockRetake,
    showCellularWarning,
    onCellularWarningProceed,
    nav,
} = useDockOrchestration({ currentView, connectionType });

// ── Carved policies (resume protocol, abandon debouncer) ──
//
// AV-R2-α: the hand-rolled `useAuroraPolicy` composable is RETIRED. glass-ui's
// `useWebGLCanvas` substrate (which Aurora composes) now OWNS the offscreen-pause
// (content-visibility + IntersectionObserver `rootMargin:200px` + `document.hidden`)
// AND the live `prefers-reduced-motion` freeze internally — every surface on the
// substrate inherits the park machinery, so the 71-line consumer policy that
// re-implemented that loop is pure duplication and is deleted. What stays a
// consumer concern is the WCAG 2.2.2 USER pause/play control, wired below.

/** The Aurora renderer seam — the WebGL pause/resume the dock toggle drives. */
interface AuroraApi {
    pause: () => void;
    resume: () => void;
}

const auroraRef = ref<AuroraApi | null>(null);
const auroraConfig = useSpeedtestAuroraConfig();

// ── AV.W16 — Aurora render-substrate consumer policy ─────────────────
//
// `<Aurora>` adapts its substrate via `renderMode` ("webgl" | "css" | "auto").
// The default is `"auto"` — glass-ui resolves it ONCE at setup to `"css"` on
// low-power / reduced-motion / save-data devices, else `"webgl"` (the warm wash
// always composites; only whether it ANIMATES adapts). We pin `"auto"`
// EXPLICITLY here so the substrate policy is legible at the consumer site rather
// than implicit in the prop default.
//
// The documented harness escape (`?aurora=css`): `resolveRenderMode`'s `"auto"`
// probe trusts `navigator.hardwareConcurrency` / `prefers-reduced-motion` /
// `saveData`, none of which flag a SOFTWARE-RASTER WebGL2 context (headless
// SwiftShader, llvmpipe) — there the WebGL path WEDGES rather than failing
// cleanly to the `onInitError` fallback. The library-root fix is the routed
// ASK-GU-AURORA-SWRASTER (a software-raster signal inside `resolveRenderMode`);
// this `?aurora=css` query is the legal compose-within HALF — an explicit,
// opt-in escape that forces the CSS substrate so a software-raster environment
// (and the W16 css-fallback witness harness) gets the static gradient wash with
// zero WebGL. It is OPT-IN ONLY (absent the query, real users keep `"auto"`),
// so it never degrades a capable device; it only honors an explicit request.
const auroraRenderMode = computed<AuroraRenderMode>(() => {
    const requested =
        typeof window !== "undefined"
            ? new URLSearchParams(window.location.search).get("aurora")
            : null;
    if (requested === "css" || requested === "webgl") return requested;
    return "auto";
});

// ── WCAG 2.2.2 background pause — seam RETIRED (DDR-AV-PAUSE / D5) ─────
//
// The `DockBackgroundToggle` pause/play control was REMOVED at W14
// (DDR-AV-W14-B0-PULSE §Pause-removal): the Aurora is decorative ambient
// motion already parked by `prefers-reduced-motion` (the conformant WCAG
// 2.2.2 path), so the visible toggle read as confusing chrome. The host
// kept inert `:show-background-toggle` + `v-model:background-paused` bindings
// (and this `auroraPaused` ref + its pause/resume watch) alive only so the
// Dock's vestigial prop/emit declarations stayed valid without an
// out-of-bounds App.vue edit. W22.2 sweeps BOTH sides coherently: the inert
// bindings + this dead seam retire together; `auroraRef` survives as the
// renderer handle the substrate mount binds (the `AuroraApi` shape), it just
// no longer drives a user-facing pause.

// AH.W3-C: rIC hydration gate for Aurora. Holding the `<Aurora>` mount off
// the first frame is what carves keyframes out of the LCP critical path —
// the `defineAsyncComponent` above defers the chunk *fetch* to template
// resolution, and this gate further defers template resolution itself to
// after first-paint settles. `useIdleReady` (glass-ui /dom, R0G-3) is the
// promoted post-mount rIC gate: rIC fires post-LCP on Chromium/Firefox;
// Safari (no rIC) falls through to a `setTimeout(0)` floor — same "after
// first paint" intent. Flips `ready` false → true exactly once.
const { ready: auroraReady } = useIdleReady({ timeout: 2000 });

// Aurora lazy-arms (glass-ui v1.9.3 `initStrategy: "deferred"` default): the
// WebGL shader compile + GPU link runs on a `requestIdleCallback` tick *after*
// first paint, behind a CSS-gradient placeholder. A consequence: a deferred
// init failure (WebGL2 unavailable, shader compile/link failure) no longer
// throws synchronously in mount — it surfaces on the microtask queue, where a
// mount-time `try/catch` (and Vue's `app.config.errorHandler`, which only
// catches synchronous reactivity/lifecycle errors) cannot see it. `<Aurora>`'s
// `onInitError` is the canonical seam for that path — routing it here keeps the
// failure visible in the dev console and leaves the gradient placeholder up as
// the natural WebGL-unavailable fallback (O invariant 24).
function onAuroraInitError(err: Error): void {
    console.error("[Aurora] deferred WebGL init failed:", err);
}

useResumeProtocol();
useFunnelAbandon(router);

// ── Dev-mode test helper ─────────────────────────────────────────────
//
// Playwright fixtures in `tests-e2e/` reach into the running app via
// `window.__speedtestDev()`. The composable installs the global hook
// once; the `import.meta.env.DEV` gate tree-shakes the entire surface
// in production. Carved out of App.vue per FA-1 §3.2 / AH-W5-a.3 —
// the dev-only block conflated test fixture wiring with App.vue's
// runtime responsibilities.
//
// AM-W7-ε — `?mock-results` DEV affordance. Loading `/?mock-results` in a
// dev build drives the speedtest store to the COMPLETE screen (badge +
// "Complete!" headline + per-metric result rows) from a representative
// `SpeedtestResults`, WITHOUT a backend run — so audits + sibling lanes
// can verify the complete surface without a live origin. The whole block
// sits under `import.meta.env.DEV`, which Vite folds to `false` in
// `--mode production`; Rollup then drops it AND the store's dev actions
// (see `features/speedtest/state/dev-mock.ts`). No production code path reaches
// either entry point.
//
// REVERSAL of DDR-AO-T3 (the natural-edge walker dev-action) — AV.W5
// / HARDENING PROTO-1. The entry point is back to the SYNCHRONOUS
// `injectMockResults()` jump. The walker existed on the premise that a
// synchronous terminal flip left `useCompletionChoreography`'s
// `isComplete` watcher permanently deaf (no rising edge → no morph →
// nulled card). The AV re-audit refuted that premise: the synchronous
// fixture renders the populated COMPLETE card on the live edge. The one
// residual subtlety the walker's scaffold also addressed survives —
// the consumer (`SpeedtestResults` → `useCompletionChoreography`) mounts
// inside the lazy-imported `SpeedtestView`, so the inject MUST wait for
// that consumer's watcher to register; otherwise `isComplete` is already
// `true` when the watcher mounts and the non-immediate watch sees no
// `false → true` edge. `router.isReady()` is the sanctioned sequencer for
// exactly that (it resolves only after the matched route's async chunk is
// mounted), so the synchronous jump lands AFTER the watcher registers and
// fires the rising edge naturally. No `{ immediate: true }` is required —
// the gap the walker dodged was a mount-ordering gap, not a Vue
// lazy-computed dep-tracking gap.
if (import.meta.env.DEV) {
    useSpeedtestDevHandle(nav);

    // `?mock-results` detection reads `window.location.search` SYNCHRONOUSLY
    // rather than `route.query`: the router's initial navigation resolves
    // asynchronously, so `route.query` can still be empty during App setup,
    // but `window.location.search` is always populated. Reading it here —
    // before any child component mounts — lets the auto-start suppression
    // win the race deterministically.
    const hasMockResults =
        typeof window !== "undefined" &&
        new URLSearchParams(window.location.search).has("mock-results");

    if (hasMockResults) {
        // Suppress the one-shot auto-start BEFORE any child mounts:
        // `useAutoStart` fires a real backend `start()` on the speedtest
        // view's `onMounted` (after a paint + rIC gate), which would clobber
        // the injected complete state. Marking the journey-store latch in
        // App setup — which runs before every child `onMounted` — makes the
        // auto-start gate's top check skip, so no live run ever competes
        // with the mock surface. This is the same session latch a real
        // auto-start sets.
        useJourneyStore().markAutoStarted();
        // Inject on App's `onMounted`, but only AFTER the matched route's
        // lazy-imported component has fully resolved + mounted. The
        // consumer (`SpeedtestResults` → `useCompletionChoreography`)
        // mounts inside `SpeedtestView`, which is dynamic-imported by
        // the RouterView. App's `onMounted` lands BEFORE the chunk
        // resolves + the consumer's `setup` runs — so without sequencing,
        // the synchronous flip would land while `isComplete` is still
        // read by a watcher that has not yet registered.
        //
        // `router.isReady()` is Vue Router's documented primitive for
        // exactly this: it resolves only after the initial navigation
        // is fully resolved AND all async-imported components on the
        // matched route are mounted. One additional `nextTick()` lets
        // the consumer's first reactive evaluation settle (the watcher
        // snapshots `isComplete = false` cleanly) before the inject flips
        // it. The synchronous flip from `injectMockResults` then reaches
        // a live, properly-registered watcher as a real `false → true`
        // rising edge and the morph plays naturally — no per-phase walk.
        //
        // The gestalt sequencer. The alternative — polling for the
        // consumer's mount marker in DOM — would be a workaround for a
        // problem Vue Router already solves.
        const speedtestStore = useSpeedtestStore();
        onMounted(async () => {
            await router.isReady();
            await nextTick();
            speedtestStore.injectMockResults?.();
        });
    }
}
</script>

<!--
  ────────────────────────────────────────────────────────────────────
  App-chassis-spine composition
  ────────────────────────────────────────────────────────────────────
  AK-W2-β2 + AL-W1-β — the App-level `<InstrumentChassis>` is the
  cascade owner across the `speedtest → survey → thankyou` journey,
  AND the housing register on quiet-content routes via the spine
  variant (AL-W1-α; glass-ui b25fb11; G-AL-D1 Path A). The variant
  selection forks the surface recipe:

    - `variant="glass"` (speedtest dial route) — invisible cascade
      pipe. The journey-station card paints its own vivid glass tier
      against the Aurora drift; the chassis behind reads as a
      transparent frame so the existing single-card composition
      reads unchanged while `--phase-color` propagates. Consumer
      overrides below crush the chassis's resting plate / bezel /
      shadow to zero on this branch.

    - `variant="spine"` (survey / thankyou routes) — the housing
      register paints. The glass-ui spine recipe (background vignette
      + 1px outer stroke + under-shadow + viewport margin) carries
      the surface; the consumer-side overrides MUST NOT crush it.
      I scope the "make-invisible" overrides via
      `[data-variant="glass"]` so the spine register survives intact.

  The rules live in a NON-SCOPED block so they reach the chassis's
  internal classes (`.instrument-dial`) without `:deep()`. Selectors
  bind to the `.app-chassis-spine` host so they don't leak to other
  `<InstrumentChassis>` consumers across the codebase.

  Per W2-β1 (glass-ui 3822f48) the chassis primitive slot-gates
  `<header>` / `<footer>` / dividers on `$slots.strip` /
  `$slots.control` presence; this App-level mount binds only `#dial`,
  so the chassis emits no strip / control / divider markup.
-->
<style>
/* AV.W3.c (T3) — @layer speedtest cascade contract. These chassis global
   rules previously relied on being UN-LAYERED to out-rank glass-ui's
   `@layer components` dial reserve (an unlayered declaration beats ANY
   layered one regardless of specificity — the prototype's case D). That
   un-layered precedence is exactly what silently DEFEATED the mobile dial
   reserve and produced the 0.3228 mobile-390 dial-CLS regress. By moving
   the whole block into `@layer speedtest{}` (declared LAST at
   @design/index.css, after glass-ui's `components` layer) the precedence
   is now explicit and ordered: speedtest > components > base. The
   `min-height: revert-layer` below then defers to the `components` reserve
   BY LAYER ORDER (prototype case E), not by un-layered luck.

   PRECONDITION verified (W3.c hard gate, b): grep + computed-style confirm
   NO competing UN-LAYERED rule sets `min-height` on the dial cell — the
   only `min-height` rules on `.instrument-dial` are (1) glass-ui's reserve,
   which is INSIDE `@layer components` (instrument-chassis.css, brace-depth
   3), and (2) these App.vue rules, which all migrate together here (no
   half-layered state). MapView's `.map-view-chassis > .instrument-dial`
   sets `height` (not `min-height`) on a different host. So the T3
   mechanism is SOUND — not the layer-exempt branch.
   See docs/decisions/DDR-AV-W3-LAYER.md. */
@layer speedtest {
    .app-chassis-spine {
        /* The chassis fills the `<main>` flex column. `<main>` is a flex
       column with `flex-1` + `min-h-0 + overflow-hidden`; the chassis
       claims the full residual vertical lane so route content + the
       inline Dock share the column cleanly. `min-h-0` lets the chassis
       shrink past its content's intrinsic block-size when the column
       is short. */
        flex: 1 1 0;
        min-height: 0;
        /* Cross-axis sizing is delegated to flex stretch, NOT a hard
       `width: 100%`. The glass-ui spine recipe insets the chassis from
       the viewport with a symmetric `margin: clamp(0.5rem, 2vw, 1.25rem)`
       (`instrument-chassis.css` `[data-variant="spine"]`). A hard
       `width: 100%` ignores those margins, so the box footprint becomes
       `100% + 2 * margin` and the right hairline of the spine outer
       stroke overflows the viewport (clipped by `<main>`'s
       `overflow-hidden`) — 8px at 390px, 20px at desktop. `align-self:
       stretch` lets the flex algorithm size the cross-axis to
       `100% - margin-left - margin-right`, so the inset is honoured
       symmetrically at every width. The `glass` variant carries zero
       margin, so stretch resolves to the full column width there —
       identical to the old `width: 100%` for the dial route. */
        align-self: stretch;
        overflow: visible;
        /* Drop the dial cell's own padding + collapse the default 2-column
       grid template to a single full-width track so the RouterView
       fills the dial cleanly. The inner route component owns its
       inset budget (the speedtest card uses `clamp(...)`; the
       thank-you card uses `clamp(...)`). */
        --instrument-dial-padding-block: 0;
        --instrument-dial-padding-inline: 0;
        --instrument-dial-columns: minmax(0, 1fr);
        --instrument-dial-gap: 0;
    }

    /* `variant="glass"` (dial route) — crush the resting glass plate to
   zero so the chassis reads as the transparent cascade pipe the
   speedtest meter card sits in front of. The spine variant skips
   this branch and the glass-ui housing recipe paints intact. */
    .app-chassis-spine[data-variant="glass"] {
        background: transparent;
        border: 0;
        box-shadow: none;
        backdrop-filter: none;
        border-radius: 0;
    }

    .app-chassis-spine > .instrument-dial {
        /* The dial cell must let the RouterView's `flex-1` wrapper fill
       its full height — grid `align-items: center` (the chassis
       primitive default) would centre the wrapper vertically and the
       inner card couldn't reach the chassis envelope. Stretch the
       dial child so the wrapper claims the cell. DESKTOP `min-height: 0`
       keeps the inner flex chain's `min-h-0` honest; the desktop 3-column
       `align-items:center` grid already reserves row height intrinsically,
       so desktop first paint is CLS-clean (§11/V2). */
        align-items: stretch;
        min-height: 0;
    }

    /* AT-R1 — the mobile dial-CLS root fix (§11/V2; the highest-value finding).
   At the ≤720px reflow the chassis stacks the dial vertically and glass-ui's
   `@layer components` R0G-2 reserve —
   `.instrument-chassis .instrument-dial { min-height: var(
   --instrument-dial-min-height-mobile, 24rem) }` paired with a
   `minmax(0,1fr) auto auto` row template — is what pins the dial's box from
   frame 0. But the rule above is UNLAYERED, and an unlayered declaration
   outranks ANY `@layer` rule regardless of specificity, so the desktop
   `min-height: 0` silently DEFEATED that reserve: the `1fr` meter row
   collapsed to the pre-hydration `<canvas>` intrinsic (~48px) and grew
   48→701px on hydration — the measured cold-prod mobile-390
   CLS 0.3228-on-`main.instrument-dial`. `revert-layer` rolls THIS layer's
   value back to the glass-ui `@layer` reserve — the idiomatic "defer to the
   library's layered default" keyword, not a re-declared magic number — and
   is Baseline (an unsupporting UA drops the declaration and degrades to the
   prior `min-height: 0`). Scoped to glass-ui's own 720px reflow breakpoint
   so desktop stays byte-identical. NOT new CSS, NOT VT. */
    @media (max-width: 720px) {
        .app-chassis-spine > .instrument-dial {
            min-height: revert-layer;
            /* DIAL-CLS-CI (deploy-gate-repair) — size-stable dial reserve.
               The `revert-layer` above defers the dial cell's `min-height` to
               glass-ui's `@layer components` reserve, which reads
               `--instrument-dial-min-height-mobile` (tokens.css canon `24rem`
               = 384px). But on THIS dial route the cell contains the speedtest
               results card, which is itself a FIXED `min/max-block-size:
               var(--chassis-max-block-size)` box — ≈652px at mobile-390, NOT
               384px. So the library's generic `24rem` reserve under-sizes the
               cell: at first paint the dial cell pins to 384px, then the card's
               652px content forces it open to ~673px and the whole below-dial
               column (the inline Dock) shifts down ~289px. THAT growth is the
               deterministic deploy-gate dial-CLS failure (mobile-390 median
               0.2055-class; LayoutShift attribution names `main.instrument-dial`
               {y:144,h:384}→{y:0,h:673} as the dominant 0.136 source, with the
               cascaded card+dock settle as the 0.176 co-source — NOT a font
               swap: `Plus Jakarta Sans|loaded` is byte-identical cold vs warm,
               the font-class shift is a residual 0.015). The race is masked on
               a fast box (the card settles before the buffered observer's first
               frame → the clean 0.04 trial) and fully exposed on the slow CI
               runner / 4× CPU throttle → the deterministic 0.2055 repeats.

               The fix is the EXACT tuning knob glass-ui documents for this case
               (`instrument-chassis.css`: "A consumer whose meter is a different
               size retunes the token — a tuning knob, not a workaround for a
               missing reserve"): retune `--instrument-dial-min-height-mobile`
               to the card's own block budget so the dial cell reserves its
               settled height from frame 0 and the card mounts into an already-
               correct envelope — zero post-hydration growth. `--chassis-max-
               block-size` is a pure CSS expression (resolves at first paint, no
               JS), and it is the IDENTICAL value the card clamps to, so the
               reserve can never drift from the content. Scoped to the dial
               route host (`.app-chassis-spine`) + the ≤720 mobile reflow only;
               desktop (3-column `align-items:center` grid, intrinsically row-
               reserved, §11/V2 CLS-clean) and other chassis consumers are
               byte-untouched. NO product-visible change: the dial already
               settles to this height — this only reserves it earlier. */
            --instrument-dial-min-height-mobile: var(--chassis-max-block-size);
        }
    }

    /* AP R1-α F1(b)+F7 — toast viewport TOP anchor. Retired the consumer
   `ol.z-toast` !important re-anchor at R0G-4: glass-ui's `<Toaster>` now
   ships a `position` prop (top-left|top-center|top-right|bottom-*), so
   `<Toaster position="top-center">` (above) anchors the viewport at the
   top-center natively — clearing the inline bottom dock at every
   breakpoint without forking the publisher's hardcoded utilities. */
}
/* ↑ close `@layer speedtest {` (AV.W3.c T3) */
</style>
